import React, {
  ChangeEvent,
  CSSProperties,
  useCallback,
  useRef,
  useState,
} from 'react'

import cn from 'classnames'

import { getNormalizedFile } from 'utils'
import { useMediaQuery } from 'utils/hooks'

import { toast } from 'App'
import ButtonIcon from 'components/ButtonIcon'
import { CustomViewZoom } from 'components/CustomViewZoom'
import { LiveIcon } from 'components/LiveIcon'
import { IconButtonPWA } from 'componentsPWA/IconButtonPWA'
import { CropImageAvatarModal } from 'containers/Profile/components/СropImageAvatarModal'

import { useStore } from 'store'

import defaultUserPhoto from 'sources/images/defaultUser.svg'
import NFTIcon from 'sources/images/NFT_Icon.png'
import plus from 'sources/images/plus.svg'

import { UserOnlineMark } from '../UserOnlineMark'

import styles from './styles.module.scss'

interface IProps {
  image?: string | null
  style?: CSSProperties
  onSaveImage?: (value: File) => void
  currentUser?: boolean
  userId?: string
  isStreaming?: boolean
  fullCircle?: boolean
  createUser?: boolean
  smallIcon?: boolean
  openSeaLink?: string
  onClick?: () => void
  withZoom?: boolean
  modePWA?: boolean
}

//Resize
const ImageProfile: React.FC<IProps> = ({
  image,
  style,
  onSaveImage,
  currentUser,
  userId,
  isStreaming,
  fullCircle,
  createUser,
  smallIcon,
  openSeaLink,
  onClick,
  withZoom,
  modePWA,
}) => {
  const {
    nft,
    profile: { viewedUser, selectedProfileId },
  } = useStore()
  const [isOpenCropImage, setIsOpenCropImage] = useState<boolean>(false)
  const [imageAvatar, setImageAvatar] = useState<string>()
  const [imageFile, setImageFile] = useState<File>()
  const width_1120 = useMediaQuery('(max-width: 1120px)')
  const editImageInput = useRef<HTMLInputElement>(null)
  const [isImageLoadingError, setIsLoadingImageError] = useState(false)

  //---▼▼▼--- EDIT IMAGE ---▼▼▼---\\
  const uploadFile = async (
    e: ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    if (e.target.files && e.target.files[0]) {
      //-▼-Check size
      if (e.target.files[0].size > 52428800) {
        return toast({
          type: 'warning',
          message: 'Maximum size limit for a profile picture is 50MB',
        })
      }
      //-▼-Check type
      if (
        e.target.files[0].type !== 'image/png' &&
        e.target.files[0].type !== 'image/jpeg' &&
        e.target.files[0].type !== 'image/gif'
      ) {
        return toast({
          type: 'warning',
          message:
            'You can only use JPG, JPEG, PNG, GIF formats for your profile picture',
        })
      }

      if (e.target.files[0].type === 'image/gif') {
        // const bodySolution = new FormData()
        // bodySolution.append('file', e.target.files[0])
        setIsOpenCropImage(false)
        //-▼-Save new file
        onSaveImage && onSaveImage(e.target.files[0])
      } else {
        setImageInfo(e.target.files[0])
      }
    }
  }

  const setImageInfo = async (file: File) => {
    setImageFile(file)
    const imageDataUrl = await readFile(file)
    setImageAvatar(imageDataUrl as string)
    setIsOpenCropImage(true)
    // const reader = new FileReader()
    // reader.readAsDataURL(file)
    // reader.onloadend = function() {
    //   setImageAvatar(reader.result as string)
    //   setIsOpenCropImage(true)
    // }
  }

  const readFile = useCallback((file: File) => {
    return new Promise((resolve, reject) => {
      try {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        getNormalizedFile(file)
          .then((normalizedFile: any) => reader.readAsDataURL(normalizedFile))
          .catch(error => reject(error))
      } catch (error) {
        reject(error)
      }
    })
  }, [])

  //---▼▼▼--- CLICK IN EDIT IMAGE ICON ---▼▼▼---\\
  const uploadImage = () => {
    editImageInput.current?.click()
  }

  const onOpenNFT = async () => {
    userId && viewedUser?.OpenSeaLink && (await nft.openNFTInfoModal(userId))
  }

  const getUserAvatar = (isError: boolean) => {
    if (createUser) {
      return plus
    }

    return image && !isError ? image : defaultUserPhoto
  }

  const handleClickAvatar = () => {
    if (isStreaming || !withZoom) {
      return onClick && onClick()
    }
  }

  const userAvatar = (
    <img
      src={getUserAvatar(isImageLoadingError)}
      alt="avatar"
      className={cn({
        [styles.avatar]: true,
        [styles.smallAvatar]: smallIcon,
        [styles.avatarModePWA]: modePWA,
      })}
      onClick={handleClickAvatar}
      onError={() => setIsLoadingImageError(true)}
    />
  )

  return (
    <>
      <CropImageAvatarModal
        imageAvatar={imageAvatar}
        imageFile={imageFile}
        isOpenCropImage={isOpenCropImage}
        setIsOpenCropImage={setIsOpenCropImage}
        onSaveImage={onSaveImage}
        uploadImage={uploadImage}
      />
      <div
        style={{ ...style }}
        className={cn({
          [styles.body]: true,
          [styles.fullBody]: image,
          [styles.emptyBody]: !image,
          [styles.fullCircle]: (fullCircle || !currentUser) && image,
          [styles.bodyStreaming]: isStreaming,
          [styles.bodyModePWA]: modePWA,
        })}
      >
        {withZoom && !isStreaming ? (
          <CustomViewZoom>{userAvatar}</CustomViewZoom>
        ) : (
          <div className={styles.avatarWrapper}>{userAvatar}</div>
        )}

        {isStreaming ? <LiveIcon style={{ bottom: '-3px' }} /> : null}

        {viewedUser?.Id !== selectedProfileId && (
          <UserOnlineMark nickName={viewedUser.NickName} />
        )}

        {openSeaLink ? (
          <img
            className={styles.isNFT}
            src={NFTIcon}
            alt=""
            onClick={onOpenNFT}
          />
        ) : null}

        {/*---▼▼▼--- EDIT IMAGE ICON ---▼▼▼---*/}
        {currentUser && (
          <>
            <input
              ref={editImageInput}
              accept=".jpg, .jpeg, .png, .gif"
              style={{ display: 'none' }}
              type="file"
              onChange={uploadFile}
            />
            {modePWA ? (
              <>
                <IconButtonPWA
                  onClick={uploadImage}
                  icon="edit"
                  buttonSX={{
                    position: 'absolute',
                    zIndex: 2,
                    bottom: 0,
                    right: 0,
                    backgroundColor: 'var(--mainColorRevert)',
                  }}
                  buttonHoverBG={'var(--mainColorRevert)'}
                  iconStyle={{ backgroundColor: 'var(--mainColor)' }}
                />
              </>
            ) : (
              <ButtonIcon
                onClick={() => {
                  uploadImage()
                }}
                icon={'edit'}
                style={{
                  zIndex: 2,
                  position: 'absolute',
                  bottom: width_1120 ? 2 : 14,
                  right: width_1120 ? -2 : 5,
                }}
              />
            )}
          </>
        )}
      </div>
    </>
  )
}

ImageProfile.displayName = 'ImageProfile'

export default ImageProfile
